import { useState, useEffect, useRef, useCallback } from 'react'
import { Loader2, X, Search } from 'lucide-react'
import { searchCie10, type Cie10Result } from '@services/cie10.service'

interface Cie10ComboboxProps {
  selectedCodes: string[]
  onCodesChange: (codes: string[]) => void
  disabled?: boolean
}

const ICD10_REGEX = /^[A-Z]\d{2}(\.\d{1,4})?$/

export function Cie10Combobox({
  selectedCodes,
  onCodesChange,
  disabled = false,
}: Cie10ComboboxProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Cie10Result[]>([])
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [descriptions, setDescriptions] = useState<Map<string, string>>(
    new Map(),
  )

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Click-outside detection
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced search
  useEffect(() => {
    if (query.length < 2) {
      setResults([])
      setIsOpen(false)
      return
    }

    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const data = await searchCie10(query)
        setResults(data)
        setIsOpen(true)
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => {
      clearTimeout(timer)
      setLoading(false)
    }
  }, [query])

  const addCode = useCallback(
    (code: string, description: string) => {
      if (selectedCodes.includes(code)) return
      onCodesChange([...selectedCodes, code])
      setDescriptions((prev) => {
        const next = new Map(prev)
        next.set(code, description)
        return next
      })
      setQuery('')
      setResults([])
      setIsOpen(false)
      inputRef.current?.focus()
    },
    [selectedCodes, onCodesChange],
  )

  const removeCode = useCallback(
    (code: string) => {
      onCodesChange(selectedCodes.filter((c) => c !== code))
    },
    [selectedCodes, onCodesChange],
  )

  const showManualEntry =
    results.length === 0 &&
    query.length >= 2 &&
    !loading &&
    ICD10_REGEX.test(query.toUpperCase())

  return (
    <div ref={containerRef} className="relative w-full">
      {/* Search input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ml-on-surface-variant/50" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => {
            if (results.length > 0 || showManualEntry) setIsOpen(true)
          }}
          placeholder="Buscar por código o diagnóstico..."
          disabled={disabled}
          className="h-10 w-full rounded-xl border border-ml-outline-variant/30 bg-ml-surface pl-9 pr-9 text-sm text-ml-on-surface placeholder:text-ml-on-surface-variant/50 focus:border-ml-primary focus:outline-none disabled:opacity-50"
        />
        {loading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ml-primary" />
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto rounded-xl border border-ml-outline-variant/20 bg-ml-surface shadow-lg">
          {results.length > 0 ? (
            results.map((item) => {
              const alreadySelected = selectedCodes.includes(item.code)
              return (
                <button
                  key={item.code}
                  type="button"
                  onClick={() => addCode(item.code, item.description)}
                  disabled={alreadySelected}
                  className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-ml-surface-container-low disabled:opacity-40"
                >
                  <span className="shrink-0 font-mono font-semibold text-ml-primary">
                    {item.code}
                  </span>
                  <span className="text-ml-on-surface-variant">
                    — {item.description}
                  </span>
                </button>
              )
            })
          ) : !showManualEntry ? (
            <div className="px-3 py-3 text-sm text-ml-on-surface-variant">
              Sin resultados para &lsquo;{query}&rsquo;
            </div>
          ) : null}

          {showManualEntry && (
            <button
              type="button"
              onClick={() => addCode(query.toUpperCase(), query.toUpperCase())}
              className="flex w-full items-center gap-2 border-t border-ml-outline-variant/20 px-3 py-2 text-left text-sm hover:bg-ml-surface-container-low"
            >
              <span className="text-ml-on-surface-variant">
                Agregar código manual:
              </span>
              <span className="font-mono font-semibold text-ml-primary">
                {query.toUpperCase()}
              </span>
            </button>
          )}
        </div>
      )}

      {/* Selected chips */}
      {selectedCodes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {selectedCodes.map((code) => (
            <span
              key={code}
              title={descriptions.get(code) ?? code}
              className="inline-flex items-center gap-1 rounded bg-ml-primary-fixed px-2 py-1 font-mono text-xs text-ml-primary"
            >
              {code}
              <button
                type="button"
                onClick={() => removeCode(code)}
                disabled={disabled}
                className="ml-0.5 hover:text-ml-error disabled:opacity-50"
                aria-label={`Eliminar ${code}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
